import type { Appearance, StripeCheckoutPaymentElementOptions } from '@stripe/stripe-js'
import type { WidgetThemeConfig } from '@tebuto/widget/theme/types'
import {
    mapTebutoMethodToStripePaymentMethodType,
    type WidgetOnlinePaymentMethod
} from './stripe-payment-method-mapping'

function resolveBorderColor(theme: WidgetThemeConfig): string {
    return theme.borderColor ?? '#e5e7eb'
}

export interface StripeWidgetAppearanceOptions {
    offlineMethodSelected?: boolean
}

export function buildStripeWidgetAppearance(
    theme: WidgetThemeConfig,
    options: StripeWidgetAppearanceOptions = {}
): Appearance {
    const borderColor = resolveBorderColor(theme)
    const offlineMethodSelected = options.offlineMethodSelected ?? false
    const activeAccordionBorderColor = offlineMethodSelected ? borderColor : theme.textPrimary

    return {
        theme: 'flat',
        variables: {
            colorPrimary: theme.primaryColor,
            colorBackground: theme.backgroundColor,
            colorText: theme.textPrimary,
            colorTextSecondary: theme.textSecondary,
            colorDanger: '#dc2626',
            borderRadius: '8px',
            fontFamily: theme.fontFamily ?? 'ui-sans-serif, system-ui, sans-serif',
            fontLineHeight: '16px',
            fontSizeBase: '14px',
            fontWeightMedium: '500',
            accordionItemLabelColorText: theme.textSecondary,
            accordionItemLabelFontSize: '14px',
            accordionItemLabelFontWeight: '500',
            accordionItemLabelSelectedColorText: theme.textPrimary,
            accordionItemLabelSelectedFontWeight: '500',
            spacingUnit: '4px'
        },
        rules: {
            '.Input': {
                border: `1px solid ${borderColor}`,
                boxShadow: 'none',
                backgroundColor: theme.backgroundColor
            },
            '.Input:focus': {
                border: `2px solid ${theme.primaryColor}`,
                boxShadow: 'none'
            },
            '.Tab': {
                border: `1px solid ${borderColor}`,
                boxShadow: 'none'
            },
            '.Tab--selected': {
                border: `2px solid ${theme.primaryColor}`,
                boxShadow: 'none'
            },
            '.TabLabel': {
                color: theme.textPrimary
            },
            '.Label': {
                color: theme.textSecondary,
                fontWeight: '500'
            },
            '.AccordionItem': {
                border: `1px solid ${borderColor}`,
                boxShadow: 'none',
                backgroundColor: 'transparent',
                color: theme.textSecondary,
                fontSize: '14px',
                fontWeight: '500',
                letterSpacing: '0',
                lineHeight: '16px',
                padding: '12px 16px'
            },
            '.AccordionItem:hover': {
                color: theme.textPrimary,
                backgroundColor: 'transparent'
            },
            '.AccordionItem--selected': {
                color: offlineMethodSelected ? theme.textSecondary : theme.textPrimary,
                border: offlineMethodSelected ? `1px solid ${borderColor}` : `2px solid ${activeAccordionBorderColor}`,
                boxShadow: 'none'
            }
        }
    }
}

function resolveWalletOptions(methods: WidgetOnlinePaymentMethod[]): StripeCheckoutPaymentElementOptions['wallets'] {
    return {
        applePay: methods.includes('apple_pay') ? 'auto' : 'never',
        googlePay: methods.includes('google_pay') ? 'auto' : 'never',
        link: 'never'
    }
}

function resolveStripePaymentMethodOrder(methods: WidgetOnlinePaymentMethod[]): string[] {
    return Array.from(
        new Set(
            methods
                .map((method) => mapTebutoMethodToStripePaymentMethodType(method))
                .filter((method): method is string => Boolean(method))
        )
    )
}

export function buildStripePaymentElementOptionsForMethods(
    methods: WidgetOnlinePaymentMethod[]
): StripeCheckoutPaymentElementOptions {
    const paymentMethodOrder = resolveStripePaymentMethodOrder(methods)

    return {
        layout: { type: 'accordion' },
        paymentMethodOrder,
        wallets: resolveWalletOptions(methods)
    }
}
